import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
// Import our safe Slot implementation instead of directly from Radix UI
import { Slot } from "./slot"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive font-univers-light forced-color-adjust-none",  {
    variants: {
      variant: {
        default:
          "bg-[#009ce5] text-primary-foreground shadow-xs hover:bg-[#0088cc]",
        destructive:
          "bg-[#DC2626] text-white shadow-xs hover:bg-[#c12121] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-white dark:bg-gray-900 shadow-xs hover:bg-[#bdbcbc]/20 hover:text-gray-900 dark:bg-gray-800/30 dark:border-gray-700 dark:hover:bg-gray-800/50",
        secondary:
          "bg-[#bdbcbc] text-secondary-foreground shadow-xs hover:bg-[#a9a8a8]",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-[#009ce5] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
